import * as React from "react";
import { DatasheetFieldComponentTextAlign } from "@common/dto/datasheet/field-component/text-align";
import { DatasheetFieldComponentGetDto } from "@common/dto/datasheet/field-component/get-dto";
import { DatasheetComponentTextStyle } from "@common/dto/datasheet/component-settings/text-style";

export function useFieldContentStyle(original: DatasheetFieldComponentGetDto) {
  const contentColor = React.useMemo(() => {
    const fontcolorNum = typeof original.fontcolor === "number" ? original.fontcolor : 0;
    const blue = fontcolorNum & 0xff;
    const green = (fontcolorNum >> 8) & 0xff;
    const red = (fontcolorNum >> 16) & 0xff;
    const hex = [
      red.toString(16).padStart(2, "0"),
      green.toString(16).padStart(2, "0"),
      blue.toString(16).padStart(2, "0"),
    ].join("");
    return `#${hex}`;
  }, [original.fontcolor]);

  const contentAlign = React.useMemo(() => {
    const align = (original as any).contentalign ?? DatasheetFieldComponentTextAlign.left;
    switch (align) {
      case DatasheetFieldComponentTextAlign.center:
        return "center" as const;
      case DatasheetFieldComponentTextAlign.right:
        return "right" as const;
      default:
        return "left" as const;
    }
  }, [original]);

  const contentFontFamily = (original as any).fontname || "Arial";
  const contentFontSize = (original as any).fontsize || 10;

  const contentTextStyles = React.useMemo<Array<DatasheetComponentTextStyle>>(() => {
    const textStyleValue = (original as any).textstyle;
    if (Array.isArray(textStyleValue)) {
      return textStyleValue.filter(
        (s): s is DatasheetComponentTextStyle =>
          s === DatasheetComponentTextStyle.bold ||
          s === DatasheetComponentTextStyle.italic ||
          s === DatasheetComponentTextStyle.underline
      );
    }
    if (typeof textStyleValue === "number" && textStyleValue !== 0 && textStyleValue !== 33) {
      const styles: Array<DatasheetComponentTextStyle> = [];
      if ((textStyleValue & DatasheetComponentTextStyle.bold) === DatasheetComponentTextStyle.bold) {
        styles.push(DatasheetComponentTextStyle.bold);
      }
      if ((textStyleValue & DatasheetComponentTextStyle.italic) === DatasheetComponentTextStyle.italic) {
        styles.push(DatasheetComponentTextStyle.italic);
      }
      if ((textStyleValue & DatasheetComponentTextStyle.underline) === DatasheetComponentTextStyle.underline) {
        styles.push(DatasheetComponentTextStyle.underline);
      }
      return styles;
    }
    return [];
  }, [original]);

  const formControlStyle = React.useMemo(
    () => ({
      fontFamily: contentFontFamily,
      fontSize: contentFontSize,
      color: contentColor,
      textAlign: contentAlign as React.CSSProperties["textAlign"],
      fontWeight: contentTextStyles.includes(DatasheetComponentTextStyle.bold) ? "bold" : undefined,
      fontStyle: contentTextStyles.includes(DatasheetComponentTextStyle.italic) ? "italic" : undefined,
      textDecoration: contentTextStyles.includes(DatasheetComponentTextStyle.underline) ? "underline" : undefined,
    }),
    [contentFontFamily, contentFontSize, contentColor, contentAlign, contentTextStyles]
  );

  return {
    contentColor,
    contentAlign,
    contentFontFamily,
    contentFontSize,
    contentTextStyles,
    formControlStyle,
  };
}

